import {Float32BufferAttribute} from 'three';
const clamp=(x,a=0,b=1)=>Math.max(a,Math.min(b,x));
// A bounded material-space pigment field. This transports color, not mass or
// pressure; the XPBD cage independently supplies the physical deformation.
export class AuroraField {
 constructor(min,max,{size=14}={}){
  this.min=[...min];this.span=max.map((v,a)=>Math.max(.001,v-min[a]));this.n=size;
  this.fields=[new Float32Array(size**3),new Float32Array(size**3)];
  this.buffers=this.fields.map(a=>new Float32Array(a.length));
  this.weights=new Float32Array(size**3);this.velocity=Array.from({length:3},()=>new Float32Array(size**3));this.reset();
 }
 reset(){for(const a of [...this.fields,...this.buffers,...this.velocity])a.fill(0);this.active=false;this.accumulator=0;this.hold=null;this.pigment=1;this.previous=null;}
 read(a,x,y,z){
  const n=this.n;x=clamp(x,0,n-1);y=clamp(y,0,n-1);z=clamp(z,0,n-1);
  const i=Math.min(n-2,Math.floor(x)),j=Math.min(n-2,Math.floor(y)),k=Math.min(n-2,Math.floor(z)),u=x-i,v=y-j,w=z-k;
  const p=i+n*(j+n*k),q=n*n;
  return ((a[p]*(1-u)+a[p+1]*u)*(1-v)+(a[p+n]*(1-u)+a[p+n+1]*u)*v)*(1-w)+((a[p+q]*(1-u)+a[p+q+1]*u)*(1-v)+(a[p+q+n]*(1-u)+a[p+q+n+1]*u)*v)*w;
 }
 sample(x,y,z,out=[0,0]){const n=this.n-1;x=(x-this.min[0])/this.span[0]*n;y=(y-this.min[1])/this.span[1]*n;z=(z-this.min[2])/this.span[2]*n;for(let c=0;c<2;c++)out[c]=this.read(this.fields[c],x,y,z);return out;}
 step(dt,hold){
  if(hold!==this.hold){if(hold){this.pigment=1-this.pigment;this.active=true;
   const n=this.n,p=(hold.anchor??hold.start).map((v,a)=>(v-this.min[a])/this.span[a]);
   for(let z=0;z<n;z++)for(let y=0;y<n;y++)for(let x=0;x<n;x++)this.weights[x+n*(y+n*z)]=Math.exp(-((x/(n-1)-p[0])**2+(y/(n-1)-p[1])**2+(z/(n-1)-p[2])**2)/.026);
  }this.hold=hold;this.previous=hold?[...hold.target]:null;}
  if(!this.active)return false;
  dt=clamp(dt,0,.1);this.accumulator+=dt;let changed=false;
  // Integrate contact time independently from transport ticks so a quick tap
  // leaves a small trace rather than disappearing or becoming a full-frame press.
  if(hold&&dt>0){const f=this.fields[this.pigment];for(let i=0;i<f.length;i++)f[i]=clamp(f[i]+this.weights[i]*dt*3.5);changed=true;}
  while(this.accumulator+1e-9>=1/30){this.accumulator-=1/30;this.advance(1/30,hold);changed=true;}
  return changed;
 }
 advance(dt,hold){
  const n=this.n,q=n*n;
  const speed=hold?hold.target.map((v,a)=>clamp((v-this.previous[a])/this.span[a]/dt,-2,2)):[0,0,0];
  if(hold)this.previous=[...hold.target];
  let peak=0;
  for(let z=0;z<n;z++)for(let y=0;y<n;y++)for(let x=0;x<n;x++){
   const i=x+n*y+q*z;
   const weight=hold?this.weights[i]:0;
   for(let a=0;a<3;a++)this.velocity[a][i]=clamp(this.velocity[a][i]*Math.exp(-dt*2.5)+speed[a]*weight*.42,-2,2);
   const bx=x-clamp(this.velocity[0][i],-2,2)*dt*(n-1),by=y-clamp(this.velocity[1][i],-2,2)*dt*(n-1),bz=z-clamp(this.velocity[2][i],-2,2)*dt*(n-1);
   for(let c=0;c<2;c++){
    const f=this.fields[c],center=f[i];
    const lap=f[i+(x<n-1?1:0)]+f[i-(x>0?1:0)]+f[i+(y<n-1?n:0)]+f[i-(y>0?n:0)]+f[i+(z<n-1?q:0)]+f[i-(z>0?q:0)]-6*center;
    const advected=this.read(f,bx,by,bz);
    const value=clamp((advected+lap*.035)*Math.exp(-dt*.29));
    this.buffers[c][i]=value;peak=Math.max(peak,value);
   }
  }
  [this.fields,this.buffers]=[this.buffers,this.fields];
  if(!hold&&peak<.004){for(const f of this.fields)f.fill(0);this.active=false;}
 }
}
export class AuroraSurface {
 constructor(min,max,meshes,options){
  this.field=new AuroraField(min,max,options);this.dirty=false;
  this.meshes=meshes.filter(m=>m.material.name==='aurora_gel').map(mesh=>{
   const position=mesh.geometry.attributes.position,original=mesh.geometry.attributes.color;
   const base=new Float32Array(position.count*3);
   for(let i=0;i<position.count;i++)for(let a=0;a<3;a++)base[i*3+a]=original?original.array[i*original.itemSize+a]:[.035,.34,.50][a];
   mesh.geometry.setAttribute('color',new Float32BufferAttribute(base.slice(),3));
   mesh.material.vertexColors=true;mesh.material.color.setRGB(1,1,1);mesh.material.needsUpdate=true;
   return {mesh,rest:position.array.slice(),base};
  });
 }
 get active(){return this.field.active;}
 step(dt,hold){const changed=this.field.step(dt,hold);this.dirty=this.dirty||changed;return changed;}
 reset(){this.field.reset();this.dirty=true;this.update();}
 update(){
  if(!this.dirty)return;this.dirty=false;
  const color=[0,0],rose=[.95,.045,.22],gold=[1,.55,.035];
  for(const {mesh,rest,base} of this.meshes){
   const attr=mesh.geometry.attributes.color;
   for(let i=0;i<attr.count;i++){
    const k=i*3;this.field.sample(rest[k],rest[k+1],rest[k+2],color);
    const sum=color[0]+color[1],mix=1-Math.exp(-sum*2.3);
    for(let a=0;a<3;a++)attr.array[k+a]=base[k+a]*(1-mix)+(sum?((rose[a]*color[0]+gold[a]*color[1])/sum)*mix:0);
   }
   attr.needsUpdate=true;
  }
 }
}
